/**
 * Theme preference.
 *
 * Three states, not two: an explicit "light"/"dark" choice, or "system" which
 * follows the OS and keeps following it as the OS changes. The stamped
 * `data-theme` attribute is what index.css switches on; its absence is the
 * signal to fall back to the `prefers-color-scheme` block.
 */

import { ref, watch } from "vue";

export type ThemePreference = "light" | "dark" | "system";

const STORAGE_KEY = "genie.theme";
/** What it was called before; read once so a preference survives the rename. */
const LEGACY_STORAGE_KEY = "jarvis.theme";

function stored(): ThemePreference {
  const value = (localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY));
  // No stored choice yet: follow the OS rather than imposing dark.
  return value === "light" || value === "dark" || value === "system" ? value : "system";
}

export const themePreference = ref<ThemePreference>(stored());

/** What is actually on screen right now, after resolving "system". */
export const resolvedTheme = ref<"light" | "dark">("dark");

const media = window.matchMedia("(prefers-color-scheme: light)");

function apply(): void {
  const preference = themePreference.value;
  const resolved =
    preference === "system" ? (media.matches ? "light" : "dark") : preference;

  // Always stamp the resolved value rather than clearing the attribute for
  // "system": frappe-ui's preset keys its own dark variants off
  // [data-theme="dark"], so it has to be present either way. The bare
  // prefers-color-scheme block in index.css only covers the pre-hydration flash.
  document.documentElement.setAttribute("data-theme", resolved);
  resolvedTheme.value = resolved;
}

export function setTheme(preference: ThemePreference): void {
  themePreference.value = preference;
  localStorage.setItem(STORAGE_KEY, preference);
}

/** Straight light↔dark flip for the header button. */
export function toggleTheme(): void {
  setTheme(resolvedTheme.value === "dark" ? "light" : "dark");
}

export function initTheme(): void {
  apply();
  watch(themePreference, apply);
  media.addEventListener("change", () => {
    if (themePreference.value === "system") apply();
  });
}
